import { Either, Option, ReadonlyArray } from "@morphism/fp";
import * as Ant from "antd";
import React from "react";
import styled, { createGlobalStyle } from "styled-components";

import { Asset } from "~/Asset";
import * as Color from "~/Color";
import { Comments } from "~/Comment";
import * as Driver from "~/Driver";
import { Graph, gql } from "~/Graph";
import { ID } from "~/ID";
import * as Layout from "~/Layout";
import { Lifecycle } from "~/Lifecycle";
import * as GenericLink from "~/Link";
import * as Log from "~/Log";
import { Media } from "~/Media";
import { Route as GenericRoute } from "~/Route";
import { Signature } from "~/Signature";
import { Time } from "~/Time";

import { Defect as Defect_, Defects as Defects_ } from "./Defect";
import { DefectType as DefectType_ } from "./DefectType";
import { DVIRs } from "./DVIRs";
import { Missing as Missing_ } from "./Missing";
import { Print as Print_ } from "./Print";
import { Requirements as Requirements_ } from "./Requirements";
import { Type as Type_, Types as Types_ } from "./Type";

export * from "./DVIRs";

export type DVIR = ID &
  Lifecycle &
  { readonly lifecycle: Lifecycle.Created } &
  DVIR.Type &
  DVIR.Defects &
  DVIR.VehicleAndTrailers &
  DVIR.AssetCommentsAndMediaResults &
  Partial<Signature> & { readonly driver: Either.ErrorOr<Driver.Driver> };

export function DVIR({
  dvir,
  onClose,
}: {
  readonly dvir: DVIR;
  readonly onClose: () => void;
}) {
  const [isOpen, setIsOpen] = React.useState(true);
  return (
    <>
      <DVIR.GlobalStyle />
      <Ant.Drawer
        closable
        visible={isOpen}
        onClose={() => setIsOpen(false)}
        afterVisibleChange={(visible) => !visible && onClose()}
        title={<DVIR.Title dvir={dvir} {...dvir} />}
        placement="right"
        width={600}
      >
        <DVIR.Header dvir={dvir} />
        <DVIR.Defects dvir={dvir} />
      </Ant.Drawer>
    </>
  );
}

export namespace DVIR {
  export import Defect = Defect_;
  export import Defects = Defects_;
  export import DefectType = DefectType_;
  export import Missing = Missing_;
  export import Print = Print_;
  export import Type = Type_;
  export import Types = Types_;
  export import Requirements = Requirements_;

  export const humanID = ({ id }: ID): string =>
    `#${id.replace(/-/g, "").slice(0, 6).toLocaleUpperCase()}`;

  export const isResolved = (dvir: DVIR): boolean =>
    pipe(
      [dvir],
      DVIRs.Statistics.fromDVIRs,
      ({ defects: { none, resolved, unresolved } }) =>
        resolved >= 1 && unresolved === 0 && none === 0
    );


  export const isUnresolved = (dvir: DVIR): boolean =>
    pipe(
      [dvir],
      DVIRs.Statistics.fromDVIRs,
      ({ defects: { none, resolved, unresolved } }) =>
        unresolved >= 1 && resolved === 0 && none === 0
    );

  export const isNoDefects = (dvir: DVIR): boolean =>
    pipe(
      [dvir],
      DVIRs.Statistics.fromDVIRs,
      ({ defects: { none, resolved, unresolved } }) =>
        none >= 1 && resolved === 0 && unresolved === 0
    );

  export const GlobalStyle = createGlobalStyle`
    .ant-collapse {
      border: none;
    }

    .ant-drawer-header {
      background: ${() => Color.gray()};
    }

    .ant-drawer-title {
      color: ${() => Color.white()};
    }

    .ant-drawer-close {
      & .anticon-close {
        color: white;
      }
    }

    .ant-collapse-item {
      margin-bottom: ${() => Layout.Block.padding()};
      border: 1px solid #dde3e8;
    }
  `;

  export namespace Route {
    export const definition = () => "/dvirs" as const;
    export const href = () => definition();
    export const use = () => GenericRoute.use(href());
  }

  export type Connection = {
    readonly totalCount: number;
    readonly pageInfo: Omit<Graph.Generated.Pagination__PageInfo, "__typename">;
    readonly edges: readonly {
      readonly cursor: string;
      readonly dvir: DVIR;
    }[];
  };
  export namespace Connection {
    export const decode = (
      encoded: Graph.Generated.Driver__VehicleInspectionReport__Connection
    ): Connection => {
      return {
        totalCount: encoded.totalCount || 0,
        pageInfo: encoded.pageInfo,
        edges:
          encoded.edges && encoded.edges.length > 0
            ? encoded.edges.map((edge) => ({
                cursor: edge.cursor,
                dvir: Fragment.decode(edge.node),
              }))
            : [],
      };
    };

    export const toDVIRs = (
      connection: Graph.Generated.Driver__VehicleInspectionReport__Connection
    ): DVIRs =>
      pipe(
        connection,
        decode,
        (decodedConnection) =>
          decodedConnection &&
          (decodedConnection?.edges ?? []).map((edge) => edge.dvir)
      );

    export const emptyConnection = (): Connection => ({
      totalCount: 0,
      pageInfo: {
        hasNextPage: false,
        hasPreviousPage: false,
      },
      edges: [],
    });
  }

  export type Fragment = Graph.Generated.DvirFragment;
  export namespace Fragment {
    export const document = () => gql`
      ${Graph.Errors.Fragment.document()}
      ${Lifecycle.Submitted.Fragment.document()}
      ${Lifecycle.Created.Fragment.document()}
      ${Driver.Fragment.document()}
      ${DVIR.AssetCommentsAndMedia.Fragment.document()}
      ${DVIR.VehiclesAndTrailers.Fragment.document()}
      ${DVIR.Defects.Fragment.document()}
      ${Signature.Fragment.document()}
      ${Media.Fragment.document()}
      ${Comments.Fragment.document()}

      fragment DVIR on Driver__VehicleInspectionReport {
        id
        type
        driver {
          ... on Error {
            ...Error
          }
          ... on Driver {
            ...Driver
          }
        }
        ...Submitted
        ...Created
        ...VehicleAndTrailers
        ...Defects
        ...Signature
        ...Media
        ...Comments
        ...AssetCommentsAndMedia
      }
    `;

    export const decode = (encoded: Graph.Generated.DvirFragment): DVIR => ({
      id: encoded.id,

      lifecycle: {
        ...Lifecycle.Submitted.decode(encoded),
        ...Lifecycle.Created.decode(encoded as unknown as Parameters<typeof Lifecycle.Created.decode>[0]),
      },

      type: encoded.type,
      driver:
        "message" in encoded.driver
          ? Either.left(Error(encoded.driver.message))
          : Either.right(Driver.Fragment.decode(encoded.driver)),

      ...Signature.decode(encoded),

      ...Defects.Fragment.decode(encoded),
      ...VehiclesAndTrailers.Fragment.decode(encoded),
      ...AssetCommentsAndMedia.Fragment.decode(encoded),
    });
  }

  export type AssetCommentsAndMediaResults = {
    readonly assetCommentsAndMedia: readonly Either.ErrorOr<
      AssetCommentsAndMedia
    >[];
  };
  export type AssetCommentsAndMedia = Lifecycle &
    Comments & {
      readonly asset: Asset.Result;
      readonly images: Media.Images;
    };

  export namespace AssetCommentsAndMedia {
    export namespace Fragment {
      export const document = () => gql`
        ${Graph.Errors.Fragment.document()}
        ${Asset.Result.Fragment.document()}
        ${Comments.Fragment.document()}
        ${Lifecycle.Submitted.Fragment.document()}
        ${Media.Fragment.document()}

        fragment AssetCommentsAndMedia on Asset__Comments__And__Media__HasMany {
          assetCommentsAndMedia {
            ... on Asset__Comments__And__Media {
              asset {
                ...Asset__Result
              }
              ...Media
              ...Comments
              ...Submitted
            }
            ... on Error {
              ...Error
            }
          }
        }
      `;

      export const decode = (
        encoded: Graph.Generated.DvirFragment
      ): AssetCommentsAndMediaResults => ({
        assetCommentsAndMedia: pipe(
          (encoded?.assetCommentsAndMedia ??
            []) as readonly (Graph.Generated.Asset__Comments__And__Media__Result | null)[],
          ReadonlyArray.filterMap(
            flow(Option.fromNullable, Option.map(decodeAssetCommentsAndMedia))
          )
        ),
      });

      export const decodeAssetCommentsAndMedia = (
        encoded: Graph.Generated.Asset__Comments__And__Media__Result
      ) =>
        "message" in encoded
          ? Either.left(Error(encoded.message))
          : Either.right({
              asset: Asset.Result.Fragment.decode(encoded.asset),
              ...Comments.Fragment.decode(encoded),
              ...Media.Image.decode(encoded),
              lifecycle: Lifecycle.Submitted.decode(encoded),
            });
    }
  }

  export type VehicleAndTrailers = {
    readonly vehicle: Asset.OptionalResult;
    readonly trailers: readonly Asset.Result[];
  };

  export namespace VehiclesAndTrailers {
    export namespace Fragment {
      export const document = () => gql`
        fragment VehicleAndTrailers on Asset__HasVehicleAndOrTrailers {
          vehicle {
            ...Asset__Result
          }
          trailers {
            ...Asset__Result
          }
        }

        ${Asset.Result.Fragment.document()}
      `;

      export const decode = (original: Fragment): VehicleAndTrailers => ({
        vehicle: original?.vehicle
          ? Asset.Result.Fragment.decode(original?.vehicle)
          : Either.right(undefined),
        trailers: pipe(
          original.trailers ?? [],
          ReadonlyArray.filterMap(
            flow(Option.fromNullable, Option.map(Asset.Result.Fragment.decode))
          )
        ),
      });
    }
  }

  export const Title = ({ id, dvir }: ID & { readonly dvir?: DVIR }) =>
    pipe(
      Option.fromNullable(dvir),
      Option.map((dvir) => (
        <>
          <DVIR.Type {...dvir} />
          &nbsp;
        </>
      )),
      Option.toUndefined,
      (type) => (
        <>
          {type}DVIR {DVIR.humanID({ id })}
        </>
      )
    );

  export function Header({ dvir }: { readonly dvir: DVIR }) {
    const driver = Either.getOrElseW(() => undefined)(dvir.driver);
    const date = Time.DateTime.toDateOnly(dvir.lifecycle.submitted);

    const Link = (driver: Driver.Driver) => (
      <GenericLink.Component
        onClick={Log.Route.use({ ...dvir.lifecycle.submitted, driver }).go}
      >
        {date}
      </GenericLink.Component>
    );

    return (
      <Header.Container>
        <Header.Left>
          <div>{driver && <Link {...driver} />}</div>
          <div>
            <Driver.Name.Component {...driver} />
          </div>
          <div>
            Created: {Time.DateTime.toDateOnly(dvir.lifecycle.created)}{" "}
            {Time.DateTime.toHMM(dvir.lifecycle.created)}
          </div>
        </Header.Left>
        <Header.Right>
          <Print dvir={dvir} />
        </Header.Right>
      </Header.Container>
    );
  }

  export namespace Header {
    export const Container = styled.div`
      display: flex;
      flex-direction: row;
      justify-content: space-between;
    `;

    export const Left = styled.div`
      display: flex;
      flex-direction: column;
    `;

    export const Right = styled.div`
      display: flex;
      justify-content: center;
      align-items: center;
    `;
  }
}
